/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { memo } from 'preact/compat';
import { tableRowStyles, tableRowStylesHC } from '../PRIVATE_Table/themes/TableRowStyles.css';
import { classNames } from '../utils/UNSAFE_classNames';
import { TableDisplayColumn } from './TableDisplayBase';
import { TableDisplayCell } from './TableDisplayCell';

/**
 * Props for the TableDisplayRow Component
 */
export type TableDisplayRowProps<K extends string | number, D, C extends string> = {
  tableId: string;
  rowKey: K;
  rowData: D;
  rowIndex: number;
  isFinalRow: boolean;
  columnsArray: TableDisplayColumn<K, D, C>[];
  rowHeaderColumnKeys: Set<C>;
  hasVerticalGridlines: boolean;
  hasHorizontalGridlines: boolean;
  hasFooters: boolean;
  hasVerticalUnderflow: boolean;
  isRtl: boolean;
  isHighContrast: boolean;
  startStickyEdge?: number;
  endStickyEdge?: number;
  firstStickyColumnIndex?: number;
  finalStickyColumnIndex?: number;
  columnBorderEdges: { hasStart: boolean; hasEnd: boolean; width: number }[];
};

/**
 * The internal component used to render a single row in Table.
 */
function TableDisplayRow<K extends string | number, D, C extends string>({
  tableId,
  rowKey,
  rowData,
  rowIndex,
  isFinalRow,
  columnsArray,
  rowHeaderColumnKeys,
  hasVerticalGridlines,
  hasHorizontalGridlines,
  hasFooters,
  hasVerticalUnderflow,
  startStickyEdge,
  endStickyEdge,
  firstStickyColumnIndex,
  finalStickyColumnIndex,
  columnBorderEdges,
  isRtl,
  isHighContrast
}: TableDisplayRowProps<K, D, C>) {
  const rowStyles = isHighContrast ? tableRowStylesHC : tableRowStyles;
  const classArray = [rowStyles.base, isFinalRow ? rowStyles.lastRowHeight : rowStyles.rowHeight];
  const classes = classNames(classArray);

  return (
    <tr
      aria-rowindex={rowIndex + 2}
      class={classes}
      role={'row'}
      data-oj-key={rowKey}
      data-oj-table-row={tableId}
      {...(typeof rowKey === 'number' && { 'data-oj-key-type': 'number' })}>
      {columnsArray.map((column: TableDisplayColumn<K, D, C>, columnIndex: number) => {
        const isSticky = column.value.sticky === 'enabled';
        let isFirstLogicalColumnIndex = false;
        let isFinalLogicalColumnIndex = false;
        if (isSticky) {
          if (startStickyEdge != null && firstStickyColumnIndex === columnIndex) {
            isFirstLogicalColumnIndex = true;
          }
          if (endStickyEdge != null && finalStickyColumnIndex === columnIndex) {
            isFinalLogicalColumnIndex = true;
          }
        }
        const cellProps = {
          tableId: tableId,
          rowData: rowData,
          field: column.value.field,
          stickyColumn: column.value.sticky,
          columnKey: column.key,
          columnIndex: columnIndex,
          rowKey: rowKey,
          rowIndex: rowIndex,
          isFinalRowIndex: isFinalRow,
          isFinalColumnIndex: columnIndex === columnsArray.length - 1,
          isFirstLogicalColumnIndex: isFirstLogicalColumnIndex,
          isFinalLogicalColumnIndex: isFinalLogicalColumnIndex,
          hasVerticalGridlines: hasVerticalGridlines,
          hasHorizontalGridlines: hasHorizontalGridlines,
          hasFooters: hasFooters,
          hasVerticalUnderflow: hasVerticalUnderflow,
          isSticky: isSticky,
          isAccRowHeader: rowHeaderColumnKeys.has(column.key),
          renderer: column.value.renderer,
          isStickyStartEdge: columnIndex === startStickyEdge,
          isStickyEndEdge: columnIndex === endStickyEdge,
          hasStartEdge: columnBorderEdges[columnIndex].hasStart,
          hasEndEdge: columnBorderEdges[columnIndex].hasEnd,
          isRtl: isRtl,
          isHighContrast: isHighContrast,
          padding: column.value.padding,
          horizontalAlignment:
            column.value.alignment?.horizontal != null
              ? column.value.alignment.horizontal
              : column.value.horizontalAlignment,
          verticalAlignment: column.value.alignment?.vertical
        };
        return <TableDisplayCell {...cellProps} />;
      })}
    </tr>
  );
}

const _areStringSetsEquivalent = (set1?: Set<string>, set2?: Set<string>) => {
  if (set1 == null) {
    return set2 == null;
  }
  if (set2 == null) {
    return false;
  }
  return set1.size === set2.size && [...set1].every((x) => set2.has(x));
};

const _areColumnsArraysEquivalent = (
  columnsArray1: TableDisplayColumn<string | number, unknown, string>[],
  columnsArray2: TableDisplayColumn<string | number, unknown, string>[]
) => {
  if (columnsArray1.length === columnsArray2.length) {
    for (let i = 0; i < columnsArray1.length; i++) {
      if (
        columnsArray1[i].key !== columnsArray2[i].key ||
        columnsArray1[i].value !== columnsArray2[i].value
      ) {
        return false;
      }
    }
    return true;
  }
  return false;
};

export const MemoizeTableDisplayRow = memo(
  TableDisplayRow,
  (
    prev: TableDisplayRowProps<string | number, unknown, string>,
    next: TableDisplayRowProps<string | number, unknown, string>
  ) => {
    if (prev && next) {
      return (
        prev.tableId === next.tableId &&
        prev.rowKey === next.rowKey &&
        prev.rowData === next.rowData &&
        prev.rowIndex === next.rowIndex &&
        prev.isFinalRow === next.isFinalRow &&
        _areColumnsArraysEquivalent(prev.columnsArray, next.columnsArray) &&
        prev.hasHorizontalGridlines === next.hasHorizontalGridlines &&
        prev.hasVerticalGridlines === next.hasVerticalGridlines &&
        (!next.isFinalRow ||
          (prev.hasFooters === next.hasFooters &&
            prev.hasVerticalUnderflow === next.hasVerticalUnderflow)) &&
        _areStringSetsEquivalent(prev.rowHeaderColumnKeys, next.rowHeaderColumnKeys) &&
        prev.startStickyEdge === next.startStickyEdge &&
        prev.endStickyEdge === next.endStickyEdge &&
        prev.firstStickyColumnIndex === next.firstStickyColumnIndex &&
        prev.finalStickyColumnIndex === next.finalStickyColumnIndex &&
        prev.columnBorderEdges === next.columnBorderEdges &&
        prev.isRtl === next.isRtl &&
        prev.isHighContrast === next.isHighContrast
      );
    }
    return false;
  }
);
